import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import React, { useContext, useEffect, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IGetAcquirers, IRestaurantBankSettingsAcquirer } from 'types';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from 'modules/CustomersAndRestaurants/Restaurants/EditRestaurant/editRestaurantState/editRestaurantContext';
import { ADYEN_BANK_SETTINGS_ID } from 'appConstants';
import { useFetchData } from 'hooks/useFetchData';
import { getBankSettingsAcquirersByRestaurantId } from 'backend/additionalInformation';
import { useTranslation } from 'react-i18next';

interface IProps {
	settingsId: string;
	onChange: (value: string | null) => void;
	setFetchedAcquirers: (value: IRestaurantBankSettingsAcquirer[]) => void;
}

export const TypeSelect: React.FC<IProps> = React.memo(({ onChange, setFetchedAcquirers, settingsId }) => {
	const { featureFlags } = useContext<IAppContext>(AppContext);
	const { restaurantId } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const { response, loading, responseError } = useFetchData<IGetAcquirers, string>(
		getBankSettingsAcquirersByRestaurantId,
		restaurantId,
	);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectType') }], [t]);

	useEffect(() => {
		response && setFetchedAcquirers(response.data.acquirers);
	}, [response]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		if (response && featureFlags.HAS_ADYEN_ACQUIRER) {
			return response.data.acquirers.map((acquirer) => ({ id: acquirer.id, name: acquirer.name || '' }));
		} else if (response && !featureFlags.HAS_ADYEN_ACQUIRER) {
			return response.data.acquirers
				.filter((acquirer) => acquirer.id !== ADYEN_BANK_SETTINGS_ID)
				.map((acquirer) => ({ id: acquirer.id, name: acquirer.name || '' }));
		}
		return [];
	}, [response]);

	return (
		<FormSelect
			testId="TypeSelect"
			label="typeLabel"
			name={`type-${settingsId}`}
			options={mappedOptions}
			onChange={onChange}
			loading={loading}
			disabled={loading}
			fetchError={responseError}
			validationRules={validationRules}
		/>
	);
});
